import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { GapItem, ControlStatusType } from "@/types/compliance";

interface GapListProps {
  gaps: GapItem[];
  isLoading: boolean;
}

const statusConfig: Record<
  ControlStatusType,
  { label: string; className: string }
> = {
  met: { label: "Met", className: "bg-green-500/20 text-green-400" },
  partially_met: {
    label: "Partial",
    className: "bg-yellow-500/20 text-yellow-400",
  },
  not_met: { label: "Not Met", className: "bg-red-500/20 text-red-400" },
  not_applicable: {
    label: "N/A",
    className: "bg-slate-500/20 text-slate-400",
  },
};

export function GapList({ gaps, isLoading }: GapListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (gaps.length === 0) {
    return (
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-400">
        No gaps identified. All applicable controls are met.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {gaps.map((gap) => {
        const cfg = statusConfig[gap.status];
        return (
          <Card key={gap.control_id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  <span className="font-mono text-sm">{gap.control_id}</span>
                  {" — "}
                  {gap.control_name}
                </CardTitle>
                <Badge className={cfg.className}>{cfg.label}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">{gap.category}</p>
            </CardHeader>
            <CardContent className="space-y-2">
              {gap.missing_requirements.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    Missing:
                  </p>
                  <ul className="ml-4 list-disc text-sm">
                    {gap.missing_requirements.map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
              {gap.recommendation && (
                <div className="flex gap-2 rounded-md bg-amber-500/10 px-3 py-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                  <p className="text-sm">{gap.recommendation}</p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
